import { useEffect } from "react";
import { applyApiFieldErrors, markErrorHandled } from "./errorMessage";

type SetFieldError = (
  field: string,
  error: { type: string; message: string },
) => void;

/**
 * T2/PLAN-009: hook compartido por los formularios que reciben un
 * `submitError` por prop desde la pantalla dueña del `useToast` (caso real
 * f4575316-5c75-4c27-83d0-340db0aafd9f) e intentan asociarlo a su input con
 * `applyApiFieldErrors`; si no pudo, se lo devuelve por
 * `onUnhandledSubmitError` para que el padre muestre el toast de siempre.
 * Repetido igual en `CatalogEntryForm`, `ProfileForm`, `MemberForm`,
 * `RewardEditor`, `OfferEditor` y `TemplateEditor` — jscpd lo marcó como
 * clon exacto entre esos formularios (quality-gate, revisión de
 * duplicación 2026-09-23).
 *
 * `markHandledOnApply` reproduce el criterio propio de `TemplateEditor.tsx`:
 * cuando el error ya quedó asociado a un campo, marca la excepción como
 * atendida para que el fallback global (`main.tsx`/`queryErrorFallback.ts`)
 * no muestre además el toast genérico un macrotask después. Los demás
 * formularios no lo hacían antes de esta extracción, así que el valor por
 * defecto lo deja apagado — ningún comportamiento cambia.
 */
export function useApiFieldErrors(
  submitError: unknown,
  setError: SetFieldError,
  fields: readonly string[],
  onUnhandledSubmitError?: (error: unknown) => void,
  markHandledOnApply = false,
): void {
  useEffect(() => {
    if (submitError === undefined) return;
    const applied = applyApiFieldErrors(submitError, setError, fields);
    if (applied) {
      if (markHandledOnApply) markErrorHandled(submitError);
    } else {
      onUnhandledSubmitError?.(submitError);
    }
    // `setError`/`onUnhandledSubmitError` son estables entre renders (la
    // primera de react-hook-form, la segunda un callback de la pantalla
    // dueña); solo interesa reaccionar cuando llega un `submitError` nuevo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);
}
